import { useEffect, useState } from 'react';

export type ThemeStyle = 'classic' | 'glass';
export type ThemeMode = 'light' | 'dark';

const STYLE_LS_KEY = 'yrp_theme_style';
const MODE_LS_KEY = 'yrp_theme_mode';

function readStored(): ThemeStyle {
  try {
    const v = localStorage.getItem(STYLE_LS_KEY);
    return v === 'glass' ? 'glass' : 'classic';
  } catch {
    return 'classic';
  }
}

function readStoredMode(): ThemeMode {
  try {
    return localStorage.getItem(MODE_LS_KEY) === 'dark' ? 'dark' : 'light';
  } catch {
    return 'light';
  }
}

function applyToRoot(style: ThemeStyle): void {
  const root = document.documentElement;
  if (style === 'glass') {
    root.setAttribute('data-theme-style', 'glass');
    root.setAttribute('data-variant', 'layered');
    root.setAttribute('data-intensity', 'medium');
  } else {
    root.removeAttribute('data-theme-style');
    root.removeAttribute('data-variant');
    root.removeAttribute('data-intensity');
  }
}

function applyModeToRoot(mode: ThemeMode): void {
  document.documentElement.classList.toggle('dark', mode === 'dark');
}

// Apply on module load so the root attributes are set before first paint of
// any component that reads them. Safe to call outside a component — the
// module is imported from main.tsx.
if (typeof window !== 'undefined') {
  applyToRoot(readStored());
  applyModeToRoot(readStoredMode());
}

export function useThemeStyle(): [ThemeStyle, (next: ThemeStyle) => void] {
  const [style, setStyle] = useState<ThemeStyle>(readStored);

  useEffect(() => {
    applyToRoot(style);
    try { localStorage.setItem(STYLE_LS_KEY, style); } catch { /* ignore */ }
  }, [style]);

  // Sync across tabs
  useEffect(() => {
    const handler = (e: StorageEvent) => {
      if (e.key === STYLE_LS_KEY) setStyle(readStored());
    };
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, []);

  return [style, setStyle];
}

export function useThemeMode(): [ThemeMode, (next: ThemeMode) => void] {
  const [mode, setMode] = useState<ThemeMode>(readStoredMode);

  useEffect(() => {
    applyModeToRoot(mode);
    try { localStorage.setItem(MODE_LS_KEY, mode); } catch { /* ignore */ }
  }, [mode]);

  useEffect(() => {
    const handler = (e: StorageEvent) => {
      if (e.key === MODE_LS_KEY) setMode(readStoredMode());
    };
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, []);

  return [mode, setMode];
}
